import { useCallback } from "react";
import useSWR from "swr";
import { Paginated, PaginationQuery } from "../types";
import { buildUrl, mutateCache, useApi } from "./api";
import { CustomError } from "./errors";
import {
  InsertProjectSharingBody,
  InsertProjectSharingResponse,
  ProjectSharing,
  UpdateProjectSharingBody,
} from "./types/projectSharings";

export const useProjectSharingsApi = () => {
  const api = useApi();

  const insertProjectSharing = useCallback(
    async (
      projectId: string,
      body: InsertProjectSharingBody,
      isAdmin?: boolean,
    ): Promise<InsertProjectSharingResponse> => {
      const url = `${isAdmin ? "/admin" : ""}/projects/${projectId}/sharings`;
      const result = await api.post<InsertProjectSharingResponse, InsertProjectSharingBody>(url, body);
      // an unregistered email gets an invite instead of a sharing
      mutateCache([
        `/projects`,
        `/admin/projects`,
        `/projects/${projectId}/sharings`,
        `/admin/projects/${projectId}/sharings`,
        `/projects/${projectId}/sharing_invites`,
        `/admin/projects/${projectId}/sharing_invites`,
      ]);
      return result;
    },
    [api],
  );

  const updateProjectSharing = useCallback(
    async (projectId: string, userId: string, body: UpdateProjectSharingBody, isAdmin?: boolean): Promise<void> => {
      const url = `${isAdmin ? "/admin" : ""}/projects/${projectId}/sharings/${userId}`;
      await api.put<UpdateProjectSharingBody>(url, body);
      mutateCache([
        `/projects`,
        `/admin/projects`,
        `/projects/${projectId}/sharings`,
        `/admin/projects/${projectId}/sharings`,
        `/projects/${projectId}`,
        `/admin/projects/${projectId}`,
      ]);
    },
    [api],
  );

  const useProjectSharings = useCallback(
    (projectId: string, query: PaginationQuery, signal: AbortSignal, isAdmin?: boolean) => {
      const url = buildUrl(`${isAdmin ? "/admin" : ""}/projects/${projectId}/sharings`, query);
      return useSWR<Paginated<ProjectSharing>, CustomError, string>(url, (url) =>
        api.get<Paginated<ProjectSharing>>({ url, signal }),
      );
    },
    [api],
  );

  const deleteProjectSharing = useCallback(
    async (projectId: string, userId: string, isAdmin?: boolean): Promise<void> => {
      await api.del(`${isAdmin ? "/admin" : ""}/projects/${projectId}/sharings/${userId}`);
      mutateCache([
        `/projects`,
        `/admin/projects`,
        `/projects/${projectId}/sharings`,
        `/admin/projects/${projectId}/sharings`,
        `/projects/${projectId}`,
        `/admin/projects/${projectId}`,
      ]);
    },
    [api],
  );

  return {
    insertProjectSharing,
    updateProjectSharing,
    useProjectSharings,
    deleteProjectSharing,
  };
};
